import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, useFieldArray, useForm } from 'react-hook-form';

import { Carteira as CarteiraModel } from '../../../models/Carteira.model';
import {
  estornarTransferenciaFormModelSchema,
  FuncionarioMin,
  Transferencias,
  VALOR_MAXIMO_TRANSFERENCIA
} from '../../../models/Transferencia.model';
import { CarteiraService } from '../../../services';
import FuncionarioService from '../../../services/Funcionario.service';
import { Colors } from '../../../shared/colors';
import CardCarteira from '../../components/CardCarteira';
import Loading from '../../components/Loading';

import ResumoTransferencia from './ResumoTransferencia';
import UsuarioTransferenciaCard from './UsuarioTransferenciaCard';

interface Props {
  funcionarios: FuncionarioMin[];

  onClose: () => void;
}

const FormEstornarTranferencia = ({ funcionarios, onClose }: Props) => {
  const form = useForm<Transferencias>({
    defaultValues: { funcionarios },
    resolver: yupResolver(estornarTransferenciaFormModelSchema)
  });

  const { fields, remove } = useFieldArray({
    control: form.control,
    name: 'funcionarios'
  });

  const [loading, setLoading] = useState(true);
  const [carteiras, setCarteiras] = useState<CarteiraModel[]>([]);
  const transf = form.watch('funcionarios');

  const valorDistibuido = transf.reduce(
    (prev, cur) => prev + (isNaN(cur.valor) ? 0 : cur.valor),
    0
  );
  const valoresValidos =
    transf.length > 0 &&
    transf.every(
      ({ valor, saldo }) =>
        Number.isFinite(valor) &&
        valor > 0 &&
        valor <= VALOR_MAXIMO_TRANSFERENCIA &&
        (saldo === undefined || valor <= saldo)
    ) &&
    valorDistibuido <= VALOR_MAXIMO_TRANSFERENCIA;

  const onValid = (data: Transferencias) => {
    setLoading(true);

    FuncionarioService.descarregarCartao(data)
      .then(onClose)
      .finally(() => setLoading(false));
  };

  const onInvalid: SubmitErrorHandler<Transferencias> = (err) => {
    console.error(err);
  };

  useEffect(() => {
    setLoading(true);
    CarteiraService.getCarteiras()
      .then(setCarteiras)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading show={loading} />;

  return (
    <FormProvider {...form}>
      <Box width={1} flex={1} minHeight={0} display="flex" flexDirection="column">
        <Box flex={1} minHeight={0} overflow="auto" p={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {carteiras.map((carteira) => {
                return <CardCarteira key={carteira.idCarteira} carteira={carteira} />;
              })}
            </Grid>
            <Grid item xs={12} mb={1}>
              <Typography color={Colors.secondary} fontSize={14} fontWeight={600}>
                {fields.length === 1 ? 'Usuário selecionado' : 'Usuários selecionados'}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {fields.map((data, i) => (
              <UsuarioTransferenciaCard
                key={data.id}
                funcionario={data}
                index={i}
                tipo="estorno"
                valor={transf[i]?.valor ?? 0}
                onRemove={() => remove(i)}
              />
            ))}
          </Grid>
        </Box>

        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gap={2}
          bgcolor="white"
          sx={{ borderTop: '1px solid #D8E0EA', flexShrink: 0 }}
        >
          <ResumoTransferencia funcionarios={transf} tipo="estorno" valorTotal={valorDistibuido} />
          {valorDistibuido > VALOR_MAXIMO_TRANSFERENCIA && (
            <Typography color="error" fontSize={12}>
              O total deve ser de até R$ 100.000,00.
            </Typography>
          )}
          <Box display="flex" gap={1.5} width={1}>
            <Button fullWidth variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!valoresValidos}
              onClick={form.handleSubmit(onValid, onInvalid)}
              sx={{
                backgroundColor: '#D92336',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#B91D2F' }
              }}
            >
              Confirmar estorno
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default FormEstornarTranferencia;
